import React from 'react'

import { RHFInput } from 'react-hook-form-input'
import * as yup from 'yup'
import useForm from 'react-hook-form'

import {
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
  Radio,
  RadioGroup,
  makeStyles,
  Chip,
  Box,
  Collapse,
} from '@material-ui/core'

import { Autocomplete } from '@material-ui/lab'

import Footer from './../Footer'
import styles from './styles'
import schemas from './../../../schemas'
import useContract from 'hooks/useContract'
import helpers from 'helpers'
import useContractConfigs from 'hooks/useContractConfigs'

const useStyles = makeStyles(styles)

const Contract = () => {
  const classes = useStyles()
  const { data, setData, nextStep } = useContract()
  const {
    register,
    setValue,
    handleSubmit,
    errors,
    unregister,
    watch,
  } = useForm({
    defaultValues: {
      ...data.contract,
      settings: {
        ...data.contract.settings,
        daysToOverdueInvoice: String(
          data?.contract?.settings?.daysToOverdueInvoice,
        ),
        invoiceInterval: helpers.formatNumber.formatNumberToString(
          data?.contract?.settings?.invoiceInterval,
        ),
        daysToGenerateNfe: helpers.formatNumber.formatNumberToString(
          data?.contract?.settings?.daysToGenerateNfe,
        ),
      },
    },
    validationSchema: schemas.contractStepConfig,
  })

  const contractEmails = watch('contractEmails')
  const settings = watch('settings')
  const payment = watch('payment')

  const {
    checkSettings,
    handleChangeServiceTax,
    handleChangeSettings,
  } = useContractConfigs(settings, setValue)

  React.useEffect(() => {
    register({ name: 'contractEmails' })
  }, [register, setValue])

  const handleChangeContractEmails = (event, contractEmails) => {
    if (contractEmails.length > 3) {
      return
    }

    setValue(
      'contractEmails',
      contractEmails.filter(email => {
        try {
          yup
            .string()
            .email()
            .validateSync(email, {})

          return true
        } catch (error) {
          return false
        }
      }),
    )
  }

  const onSubmit = contract => {
    setData(data => ({ ...data, contract: { ...data.contract, ...contract } }))
    nextStep()
  }

  return (
    <div className={classes.root}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className={classes.formFields}>
          <Box mb={6}>
            <Box mb={2} mt={2}>
              <Typography variant="h5" color="textSecondary">
                Assinatura
              </Typography>
            </Box>
            <div>
              <RHFInput
                as={
                  <RadioGroup aria-label="payment" name="payment" row>
                    <FormControlLabel
                      value="prepaid"
                      control={<Radio />}
                      label="Pré-paga"
                    />
                    <FormControlLabel
                      value="postpaid"
                      control={<Radio />}
                      label="Pós-paga"
                    />
                  </RadioGroup>
                }
                name="payment"
                register={register}
                setValue={setValue}
                mode="onChange"
              />
            </div>

            <Box
              mt={2}
              display="flex"
              flexDirection={{
                xs: 'column',
                sm: 'row',
              }}
            >
              <Box mr={5} width="100%">
                {payment === 'postpaid' ? (
                  <RHFInput
                    as={
                      <TextField
                        label="Dia de vencimento da assinatura"
                        margin="dense"
                        type="number"
                        error={!!errors.dayExpiration}
                        helperText={
                          errors?.dayExpiration?.message ||
                          'Dia base para emissão e vencimento das faturas recorrentes.'
                        }
                        inputProps={{ min: 1, max: 31 }}
                        fullWidth
                      />
                    }
                    name="dayExpiration"
                    register={register}
                    unregister={unregister}
                    setValue={setValue}
                    mode="onChange"
                  />
                ) : (
                  <TextField
                    label="Dia de vencimento da assinatura"
                    margin="dense"
                    value="Um dia após ativação"
                    helperText="Dia base para emissão e vencimento das faturas recorrentes."
                    size="small"
                    disabled
                    fullWidth
                  />
                )}
              </Box>
              <RHFInput
                as={
                  <TextField
                    label="URL para envio de notificações"
                    margin="dense"
                    error={!!errors.urlNotification}
                    helperText={
                      errors?.urlNotification?.message ||
                      'Envio de notificações/atualizações para o sistema de gestão do vendedor nos casos de integração por API.'
                    }
                    fullWidth
                  />
                }
                name="urlNotification"
                register={register}
                setValue={setValue}
                mode="onChange"
              />
            </Box>

            <Autocomplete
              size="small"
              value={contractEmails}
              options={[]}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option}
                    {...getTagProps({ index })}
                  />
                ))
              }
              renderInput={params => (
                <TextField
                  {...params}
                  label="Emails de notificação"
                  variant="outlined"
                  margin="dense"
                  helperText="Informe até 3 e-mails adicionais para receber as faturas e demais e-mails de notificação sobre a assinatura."
                  fullWidth
                />
              )}
              onChange={handleChangeContractEmails}
              autoSelect
              freeSolo
              multiple
            />
          </Box>

          <Box mb={6}>
            <Box mb={2} mt={2}>
              <Typography variant="h5" color="textSecondary">
                Faturas
              </Typography>
            </Box>

            <Box
              mt={2}
              display="flex"
              flexDirection={{
                xs: 'column',
                sm: 'row',
              }}
            >
              <Box mr={5} width="100%">
                <RHFInput
                  as={
                    <TextField
                      label="Dias para vencimento da fatura"
                      margin="dense"
                      type="number"
                      error={!!errors['settings.daysToOverdueInvoice']}
                      helperText={
                        errors['settings.daysToOverdueInvoice']?.message ||
                        'Dias que serão adicionados para o vencimento das faturas, com base no vencimento da assinatura. Ex: se a assinatura tem vencimento todo dia 15 e for configurado 5 dias para vencimento da fatura, o vencimento de cada fatura será todo dia 20 do mês.'
                      }
                      inputProps={{ min: 0 }}
                      fullWidth
                    />
                  }
                  name="settings.daysToOverdueInvoice"
                  register={register}
                  setValue={setValue}
                  mode="onChange"
                />
              </Box>
              <Box width="100%">
                <RHFInput
                  as={
                    <TextField
                      label="Dias para geração da fatura antes do vencimento da assinatura"
                      type="number"
                      margin="dense"
                      error={!!errors['settings.invoiceInterval']}
                      helperText={errors['settings.invoiceInterval']?.message}
                      inputProps={{ min: 0 }}
                      disabled={checkSettings['invoiceInterval']}
                      fullWidth
                    />
                  }
                  register={register}
                  setValue={setValue}
                  name="settings.invoiceInterval"
                  mode="onChange"
                />
                <FormControlLabel
                  control={<Checkbox />}
                  label={`Utilizar configuração geral do vendedor (${data.generalSettings.invoiceInterval} dias)`}
                  checked={checkSettings['invoiceInterval']}
                  onChange={event =>
                    handleChangeSettings(
                      event,
                      'invoiceInterval',
                      data?.contract?.settings.invoiceInterval,
                    )
                  }
                />
              </Box>
            </Box>
          </Box>

          <Box mb={6}>
            <Box mb={2} mt={2}>
              <Typography variant="h5" color="textSecondary">
                Notas fiscais
              </Typography>
            </Box>

            <Typography variant="h6" color="textSecondary">
              Deseja emitir notas fiscais das faturas?
            </Typography>
            <RHFInput
              as={
                <RadioGroup
                  aria-label="settings.generateServiceTaxInvoices"
                  name="settings.generateServiceTaxInvoices"
                  row
                >
                  <FormControlLabel
                    value="true"
                    control={<Radio />}
                    label="Sim"
                    checked={settings.generateServiceTaxInvoices}
                  />
                  <FormControlLabel
                    value="false"
                    control={<Radio />}
                    label="Não"
                    checked={!settings.generateServiceTaxInvoices}
                  />
                </RadioGroup>
              }
              name="settings.generateServiceTaxInvoices"
              register={register}
              setValue={setValue}
              onChange={handleChangeServiceTax}
              mode="onChange"
            />

            <Collapse in={settings.generateServiceTaxInvoices}>
              <Box mb={2}>
                <RHFInput
                  as={
                    <TextField
                      label="Dias para geração da NFe antes do vencimento da fatura"
                      type="number"
                      margin="dense"
                      error={!!errors['settings.daysToGenerateNfe']}
                      helperText={errors['settings.daysToGenerateNfe']?.message}
                      inputProps={{ min: 0 }}
                      disabled={checkSettings['daysToGenerateNfe']}
                      fullWidth
                    />
                  }
                  register={register}
                  setValue={setValue}
                  name="settings.daysToGenerateNfe"
                  mode="onChange"
                />
                <FormControlLabel
                  control={<Checkbox />}
                  label={`Utilizar configuração geral do vendedor (${
                    data.generalSettings.generateNfeOnInvoiceCreation
                      ? 'Na criação da fatura'
                      : data.generalSettings.daysToGenerateNfe + ' dias'
                  })`}
                  checked={checkSettings['daysToGenerateNfe']}
                  onChange={event =>
                    handleChangeSettings(
                      event,
                      'daysToGenerateNfe',
                      data?.contract?.settings.daysToGenerateNfe,
                    )
                  }
                />
              </Box>
              <RHFInput
                as={
                  <TextField
                    label="Descrição fixa nas notas fiscais"
                    margin="dense"
                    type="text"
                    error={!!errors['settings.fixedTaxInvoiceDescription']}
                    helperText={
                      errors['settings.fixedTaxInvoiceDescription']?.message
                    }
                    multiline
                    rows={2}
                    fullWidth
                  />
                }
                name="settings.fixedTaxInvoiceDescription"
                register={register}
                setValue={setValue}
                mode="onChange"
              />
            </Collapse>
          </Box>
        </div>
        <Footer />
      </form>
    </div>
  )
}

export default Contract
